import React from 'react';
import { Box, Card, Chip, Grid, IconButton, LinearProgress, Typography } from '@mui/material';
import { BsThreeDotsVertical } from 'react-icons/bs';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import TitleBar from 'components/TitleBar';
import ListPopUp from './qualitylistPopUp';
import { useState } from 'react';
import moment from 'moment';
import StatusBadge from 'components/badge/StatusBadge';
import AssetError from './AssetError';

const Quality = ({ ncrData, isLoading, error, assetError }) => {

    const [openPopup, setOpenPopup] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");

    const handleGaugeClick = (statusName) => {
        const status = getStatus(statusName)
        setSelectedStatus(status);
        setOpenPopup(true);
    };

    const getStatus = (status) => {
        switch (status) {
            case "Open NCR":
                return "Open";
            case "Closed NCR":
                return "Closed"
            default:
                return "";
        }
    };
    const apiData = ncrData?.data?.data;

    const openCount = Number(apiData?.statusCounts?.Open) || 0;
    const closedCount = Number(apiData?.statusCounts?.Closed) || 0;
    const total = Number(apiData?.total) || (openCount + closedCount);

    const hasData = (openCount + closedCount) > 0;

    const maxGauge = Math.max(openCount + closedCount, 1);

    const columns = [
        {
            name: "ID",
            field: "customId",
            width: "130px",
            sticky: true,
        },
        {
            name: "Letter Number",
            field: "letterNumber",
            width: "200px",
            sticky: true,
        },
        {
            name: "No. Of Observations",
            field: "numberOfObservationsRaised",
            sticky: true,
        },
        {
            name: "Asset",
            field: "asset",
        },
        {
            name: "Letter Ref.",
            field: "letterReference",
            width: "200px",
        },
        {
            name: "Area/Item Of Inspection",
            field: "areaOrItemOfInspection",
            width: "200px",
        },
        {
            name: "NCR/SO",
            field: "ncRorSO",
        },
        {
            name: "Date Of Issued Letter",
            field: "dateOfIssuedLetter",
            renderer: (row) => (
                row?.dateOfIssuedLetter ? moment.utc(row.dateOfIssuedLetter).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Observations (summary)",
            field: "observationSummary",
            width: "360px",

            // renderer: (row) => (
            //     <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            //         {row?.observations}
            //     </Box>
            // ),
        },
        {
            name: "Remarks/Action Required",
            field: "remarksOrActionRequired",
            width: "360px",

            // renderer: (row) => (
            //     <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            //         {row?.remarks}
            //     </Box>
            // ),
        },
        {
            name: "Response Shared",
            field: "isResponseSharedWithAuthority",
            renderer: (row) => (
                <>{row?.isResponseSharedWithAuthority === true ? "Yes" : "No"}</>
            )
        },
        {
            name: 'NCR Status',
            field: 'ncrStatus',
            renderer: (row) => (
                <StatusBadge status={row?.ncrStatus} />
            )
        },
        {
            name: "Date Of Closure",
            field: "dateOfClosure",
            renderer: (row) => (
                row?.dateOfClosure ? moment.utc(row.dateOfClosure).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Ageing",
            field: "ageingDays",
        },
        {
            name: "TAT (In Days)",
            field: "tatInDays",
        },
        {
            name: "Date Of Response",
            field: "dateOfResponse",
            renderer: (row) => (
                row?.dateOfResponse ? moment.utc(row.dateOfResponse).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "GRIL Response",
            field: "grilResponse",
            width: "200px",
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {row?.grilResponse}
                </Box>
            ),
        },
        {
            name: "No. Of Observations Adressed",
            field: "numberOfObservationsAddressed",
        },
        {
            name: "Reference Letter",
            field: "responseLetterReference",
        },
    ];

    const qualityData = [
        {
            label: "Total NCR / SO",
            value: total,
            color: "#0088FF",
            type: "total",
        },
        {
            label: "Total Open NCR / SO",
            value: openCount,
            color: "#E65F2B",
        },
        {
            label: "Total Closed NCR / SO",
            value: closedCount,
            color: "#1A932E",
        },
    ];

    const chartOptions = {
        chart: {
            type: "solidgauge",
            height: 220,
        },

        title: null,

        pane: {
            center: ["50%", "60%"],
            size: "110%",
            startAngle: -120,
            endAngle: 120,
            background: [
                {
                    outerRadius: "110%",
                    innerRadius: "92%",
                    backgroundColor: "#EEF0FF",
                    shape: "arc",
                },
                {
                    outerRadius: "91%",
                    innerRadius: "73%",
                    backgroundColor: "#F5F7FF",
                    shape: "arc",
                },
            ],
        },

        yAxis: {
            min: 0,
            max: maxGauge,
            lineWidth: 0,
            tickPositions: [],
        },

        plotOptions: {
            solidgauge: {
                linecap: "round",
                dataLabels: { enabled: false },
                cursor: "pointer",
                point: {
                    events: {
                        click: function () {

                            handleGaugeClick(this.series.name);
                        }
                    }
                }
            },
            pie: {
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                },
            },
        },

        series: [
            ...(hasData
                ? [
                    {
                        type: "solidgauge",
                        name: "Open NCR",
                        data: [
                            {
                                y: openCount,
                                color: "#E65F2B",
                                radius: "110%",
                                innerRadius: "92%",
                            },
                        ],
                    },
                    {
                        type: "solidgauge",
                        name: "Closed NCR",
                        data: [
                            {
                                y: closedCount,
                                color: "#1A932E",
                                radius: "91%",
                                innerRadius: "73%",
                            },
                        ],
                    },
                ]
                : []),

            {
                type: "pie",
                size: "40%",
                innerSize: "100%",
                data: [1],
                dataLabels: {
                    format: hasData
                        ? `
                        <div style="text-align:center;">
                            <div style="font-size:20px;font-weight:700;color:#343A40;">
                                ${total}
                            </div>
                            <div style="font-size:14px;color:#5F666C;">
                                Total NCR / SO
                            </div>
                        </div>`
                        : `
                        <div style="text-align:center;">
                            <div style="font-size:16px;font-weight:600;color:#8A9197;">
                                No data
                            </div>
                            <div style="font-size:13px;color:#A0A6AD;">
                                Open & Closed = 0
                            </div>
                        </div>`,
                    distance: -40,
                },
                enableMouseTracking: false,
                borderWidth: 0,
            },
        ],

        credits: { enabled: false },
        exporting: { enabled: false },
    };

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "3px",
                    border: "1px solid #E5E5E5",
                    p: 0,
                    height: "100%",
                    pb: 2,
                    boxSizing: "border-box",
                }}
            >
                <Grid container>
                    <TitleBar
                        title="Quality"
                        description="Track overall quality health for teams to quickly spot and act on emerging issues."
                    />
                </Grid>

                {!isLoading && !error && apiData && (
                    <Grid container spacing={3} sx={{ p: 2, pb: 0 }}>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Box sx={{ display: "grid", gap: 1.5 }}>
                                {qualityData.map((item) => (
                                    <Box key={item.label}>
                                        <Typography
                                            sx={{
                                                fontFamily: "Outfit, sans-serif",
                                                fontWeight: 400,
                                                fontSize: "13px",
                                                lineHeight: "100%",
                                                letterSpacing: "0px",
                                                color: item.color,
                                            }}
                                        >
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: "Outfit, sans-serif",
                                                fontWeight: 500,
                                                fontSize: "24px",
                                                lineHeight: "100%",
                                                letterSpacing: "0px",
                                                color: item.color,
                                                mt: "5px",
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, md: 7 }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                options={chartOptions}
                            />
                        </Grid>
                    </Grid>
                )}

                {isLoading && (
                    <Box sx={{ p: 3 }}>Loading data...</Box>
                )}

                {error && (
                    <Box sx={{ p: 3 }}>
                        Error loading data: {error?.status} -{" "}
                        {error?.data?.message || "Please try again."}
                    </Box>
                )}
                {assetError && <AssetError />}
            </Card>
            {
                openPopup &&
                <ListPopUp
                    open={openPopup}
                    handleClose={() => setOpenPopup(false)}
                    data={selectedStatus}
                    columns={columns}

                />
            }
        </>
    );
};

export default Quality;